import { ReactNode } from 'react';

import { AuthenticatorTransportType, PublicKeyCredentialTypeType } from '@treezor/treezor-sca';

import { IWallet } from '@/features/wallets/store/models/wallet.model';

import { IState } from '@/store/types/search.types';
import { IAdvancedRoleMetadata, TAdvancedRoleSlug } from '@/features/roles/store/models/advancedRole.model';

export type TFavoriteItemType = 'wallet';

type TFavoriteItem<T, U> = {
  type: T;
} & U;

export type TWalletFavorite = Pick<IWallet, 'walletId' | 'iban' | 'userId'> & {
  id: number;
  userName: string;
  name: string;
};

export type TFavorite = TFavoriteItem<TFavoriteItemType, TWalletFavorite>;

interface ISupportUserMetadatas {
  role?: IAdvancedRoleMetadata;
  favorites?: TFavorite[];
  edit?: IState<{ id: number }>;
}

export interface ISupportUserRequest {
  id?: string;
  username?: string;
  password?: string;
  repeatPassword?: string;
  passphrase?: string;
  repeatPassphrase?: string;
  samlKey?: string;
  scopes?: string[];
  metadata?: ISupportUserMetadatas;
}

export interface IAuthPasskey {
  secureKeyId?: string;
  supportUserId?: string;
  secureKeyTag?: string;
  authenticator?: {
    type?: PublicKeyCredentialTypeType;
    transports?: AuthenticatorTransportType[];
    publicKeyCredentialId?: string;
    aaguid: string;
    attestationType: string;
    // eslint-disable-next-line
    backupEligible: boolean;
    // eslint-disable-next-line
    backupStatus: boolean;
    counter: number;
    credentialPublicKey: string;
    trustPath: {
      type: string;
    };
    userHandle: string;
    // eslint-disable-next-line
    uvInitialized: boolean;
  };
  createdDate?: string;
}

export const supportUserStatus = {
  LOCK: 'LOCK',
  UNLOCK: 'UNLOCK',
} as const;

export const supportUserLockReason = {
  TEMPORARY_BLOCK: 'TEMPORARY_BLOCK',
  DELETED: 'DELETED',
} as const;

export type TSupportUserStatus = keyof typeof supportUserStatus;

export type TSupportUserLockReason = keyof typeof supportUserLockReason;

export interface ISupportUser {
  id: string;
  username: string;
  samlKey?: string;
  clients?: ISupportUserClient[];
  keyPairId?: string;
  wrongPwdAttempts?: number;
  metadata?: ISupportUserMetadatas;
  secureKey?: IAuthPasskey;
  accessExpirationDate?: string;
  statusUpdatedAt?: string;
  status?: TSupportUserStatus;
  lockReason?: TSupportUserLockReason;
}

export interface ISupportUserForm {
  id?: string;
  username?: string;
  password?: string;
  repeatPassword?: string;
  passphrase?: string;
  repeatPassphrase?: string;
  samlKey?: string;
  role?: TAdvancedRoleSlug;
  metadata?: ISupportUserMetadatas;
}

export interface ISupportUserClient {
  clientId: string;
  scopes: string[];
  resourceSet: any[];
  lastLoginAt: null;
  acceptedAt: string;
}

export const dashboardUserStatus = {
  ACTIVE: 'active',
  BLOCKED: 'blocked',
  EXPIRED: 'expired',
  NEAR_EXPIRED: 'nearExpired',
} as const;

export type TDashboardUserStatus = (typeof dashboardUserStatus)[keyof typeof dashboardUserStatus];

export interface ISupportUserFilter {
  cursor?: string;
  status?: TSupportUserStatus;
  lockReason?: TSupportUserLockReason;
}

export const headers = [
  'username',
  'role',
  'reviewStatus',
  'status',
  'suspensionDate',
  '2fa',
  'lastactive',
  'adhesion',
  'actions',
] as const;

type THeadersKeys = (typeof headers)[number];

export const dashboardUserHeadersList: THeadersKeys[] = [...headers];

export type TDashboardUsersRowType = {
  [key in THeadersKeys]: ReactNode;
};

export const twoFaStatus = ['enabled', 'disabled'] as const;
export type T2FaStatus = (typeof twoFaStatus)[number];

export interface IDashboardUsersFiltersForm {
  username?: string;
  role?: TAdvancedRoleSlug;
  status?: TDashboardUserStatus;
  '2faStatus'?: T2FaStatus;
}

export const ssoStatusOptions = {
  active: 'active',
  inactive: 'inactive',
  SAMLKeyMissing: 'SAMLKeyMissing',
} as const;

export type TSsoStatus = (typeof ssoStatusOptions)[keyof typeof ssoStatusOptions];
